import { Reveal } from "@components/ui";
import type { ReactNode } from "react";

// --- Inline SVG Icons ---

function HouseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10.5L12 3l9 7.5V20a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 20V10.5Z" />
      <path d="M9 21V12h6v9" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" />
      <path d="M5 21V7l8-4v18" />
      <path d="M19 21V11l-6-4" />
      <path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01" />
    </svg>
  );
}

function FactoryIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20V8l4.5 3V8l4.5 3V8l4.5 3h0V4h4.5a1 1 0 0 1 1 1v15H2Z" />
      <path d="M6 16h.01M10 16h.01M14 16h.01M18 16h.01" />
    </svg>
  );
}

function IdCardIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <circle cx="8" cy="12" r="2" />
      <path d="M14 10h4M14 14h4" />
      <path d="M5 19c.5-1.5 1.5-2.5 3-2.5s2.5 1 3 2.5" />
    </svg>
  );
}

function KeyHouseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 10a3.5 3.5 0 1 0-5 3.15V19l1.5 1.5L10 19l1.5 1.5L13 19v-5.85A3.5 3.5 0 0 0 12 10Z" />
      <circle cx="9.5" cy="10" r="1" />
      <path d="M15 3l4 3.5L15 10" />
      <path d="M19 6.5h-4" />
    </svg>
  );
}

function BriefcaseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      <path d="M2 12h20M12 12v4" />
    </svg>
  );
}

function BlueprintIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2" />
      <path d="M7 3v18M3 7h18M3 17h18M17 7v10" />
      <path d="M10 10h4v4h-4z" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h8M8 9h2" />
    </svg>
  );
}

// --- Data ---

const applicantTypes: { title: string; description: string; icon: ReactNode }[] = [
  {
    title: "Residential Units",
    description: "Apartments and villas with LPG or SNG systems",
    icon: <HouseIcon />,
  },
  {
    title: "Commercial Clients",
    description: "Restaurants, caf\u00e9s, bakeries, and laundries",
    icon: <BuildingIcon />,
  },
  {
    title: "Industrial Facilities",
    description: "Factories and manufacturing plants",
    icon: <FactoryIcon />,
  },
];

const documentsRow1: { title: string; description: string; icon: ReactNode }[] = [
  {
    title: "Individual",
    description: "Emirate ID (copy front & back)",
    icon: <IdCardIcon />,
  },
  {
    title: "Tenant / Owner",
    description: "Tenancy Contract or Title Deed",
    icon: <KeyHouseIcon />,
  },
  {
    title: "Business / Commercial",
    description: "Trade License",
    icon: <BriefcaseIcon />,
  },
];

const documentsRow2: { title: string; description: string; icon: ReactNode }[] = [
  {
    title: "Developer / Facility",
    description: "Layout drawing / as built plan",
    icon: <BlueprintIcon />,
  },
  {
    title: "Others",
    description: "NOC from Landlord(if tenant) or Building Management",
    icon: <DocumentIcon />,
  },
];

// --- Cards ---

interface ApplicantCardProps {
  title: string;
  description: string;
  icon: ReactNode;
}

function ApplicantCard({ title, description, icon }: ApplicantCardProps) {
  return (
    <div className="group rounded-[14px] bg-white border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 overflow-hidden flex items-center gap-4 p-4 md:p-5">
      <div className="w-10 h-10 md:w-11 md:h-11 rounded-[10px] bg-[#f24d2b]/[0.08] text-[#f24d2b] flex items-center justify-center shrink-0 group-hover:bg-[#f24d2b] group-hover:text-white transition-colors duration-300">
        {icon}
      </div>
      <div className="flex flex-col gap-0.5">
        <h3 className="text-[15px] md:text-[17px] lg:text-[19px] tracking-[-0.04em] font-semibold text-[#090d2c] font-albert-sans leading-[1.2]">
          {title}
        </h3>
        <p className="text-[12px] md:text-[13px] lg:text-[14px] tracking-[-0.04em] text-[#62636c] font-albert-sans leading-[1.4]">
          {description}
        </p>
      </div>
    </div>
  );
}

interface DocumentCardProps {
  title: string;
  description: string;
  icon: ReactNode;
}

function DocumentCard({ title, description, icon }: DocumentCardProps) {
  return (
    <div className="group rounded-[12px] bg-white border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-all duration-300 overflow-hidden flex items-center gap-3.5 p-3.5 md:p-4">
      <div className="w-9 h-9 md:w-10 md:h-10 rounded-[8px] bg-[#122d63]/[0.08] text-[#122d63] flex items-center justify-center shrink-0 group-hover:bg-[#122d63] group-hover:text-white transition-colors duration-300">
        {icon}
      </div>
      <div className="flex flex-col gap-0.5">
        <h4 className="text-[14px] md:text-[15px] lg:text-[16px] tracking-[-0.04em] font-semibold text-[#1e1f24] font-albert-sans leading-[1.2]">
          {title}
        </h4>
        <p className="text-[12px] md:text-[13px] lg:text-[14px] tracking-[-0.04em] text-[#62636c] font-albert-sans leading-[1.4]">
          {description}
        </p>
      </div>
    </div>
  );
}

// --- Section ---

export function WhoCanApplySection() {
  return (
    <section className="w-full bg-white overflow-hidden px-5 md:px-10 lg:px-[180px] py-12 md:py-16 lg:py-[100px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-8 lg:gap-10">
        {/* Header */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-3 md:gap-4">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/[0.08] text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Eligibility
            </span>
            <h2 className="text-center text-[24px] md:text-[36px] lg:text-[48px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
              Who Can Apply
            </h2>
            <p className="max-w-[614px] text-center text-[14px] md:text-base lg:text-[18px] tracking-[-0.04em] text-[#62636c] font-albert-sans leading-[1.4]">
              We serve residential, commercial, and industrial customers across
              the UAE.
            </p>
          </div>
        </Reveal>

        {/* Applicant Type Cards */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {applicantTypes.map((type, index) => (
            <Reveal key={type.title} direction="up" delay={index * 100}>
              <ApplicantCard {...type} />
            </Reveal>
          ))}
        </div>

        {/* Required Documents Sub-Section */}
        <Reveal direction="up" className="w-full">
          <div className="w-full rounded-[16px] bg-[#f8f9fb] flex flex-col items-start p-4 md:p-6 lg:p-8 gap-5 md:gap-6">
            {/* Documents Header */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-[10px] bg-[#122d63]/[0.08] text-[#122d63] flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z" />
                  <path d="M14 2v6h6M9 15l2 2 4-4" />
                </svg>
              </div>
              <h3 className="text-[16px] md:text-[18px] lg:text-[22px] tracking-[-0.04em] font-semibold text-[#1e1f24] font-albert-sans leading-[1.2]">
                Required Documents
              </h3>
            </div>

            {/* Documents Grid — all in one */}
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[...documentsRow1, ...documentsRow2].map((doc, index) => (
                <Reveal key={doc.title} direction="up" delay={index * 60}>
                  <DocumentCard {...doc} />
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
